import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, notification, Spin } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import { TDivisionResponse, TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import moment from 'moment';
import { PICKER_DATE_MASK, PICKER_TIME_MASK } from 'shared/constants';
import { IPollUpdateRequest, TQuestionInPoll } from 'shared/types/poll.types';
import { PollTabs } from 'shared/enums/poll';
import {
  useDeletePollMutation,
  useGetPollByIdQuery,
  useUpdatePollMutation,
  useUpdatePollStatusMutation,
} from 'app/services/edpApiV1/poll';
import { uiModel } from 'entities/ui';
import { checkDate } from 'shared/helpers/date';
import { addNotifications } from 'shared/helpers/utils';
import ModalCancelSubmit from '../../../shared/components/modalCancelSubmit/ModalCancelSubmit';
import RequredField from '../../../shared/components/title/Title';
import PickDivisions from '../createPoll/pickDivisions/PickDivisions';
import PollDates from '../createPoll/pollDates/PollDates';
import QuestionsSection from '../createPoll/questionsSection/QuestionsSection';
import QuestionDrawer from '../questionDrawer/QuestionDrawer';
import style from './index.module.scss';

const UpdatePoll: FC = () => {
  const { id } = useParams();
  const breadCrumbs = [
    { name: 'Опросы', link: '/polls' },
    { name: 'Редактировать опрос', link: `/polls/update/${id}` },
  ];
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const me = useAppSelector(state => state.entities.viewer.me);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: currentPoll, isFetching: isPollFetching, isLoading: isPollLoading } = useGetPollByIdQuery({ id: +id! });

  const [title, setTitle] = useState<string>('');
  const [auto, setAuto] = useState<boolean>(false);
  const [text, setText] = useState<string>('');
  const [divisions, setDivisions] = useState<TDivisionResponse[]>([]);
  const [organizations, setOrganizations] = useState<TOrganizationResponse[]>([]);
  const [startTime, setStartTime] = useState<string | string[] | undefined>();
  const [endTime, setEndTime] = useState<string | string[] | undefined>();
  const [startDate, setStartDate] = useState<string | string[] | undefined>();
  const [endDate, setEndDate] = useState<string | string[] | undefined>();
  const [pollQuestions, setPollQuestions] = useState<TQuestionInPoll[]>([]);
  const [isDrawerCreateOpen, setIsDrawerCreateOpen] = useState<boolean>(false);

  const [updatePollQuery, { isLoading: isUpdateLoading }] = useUpdatePollMutation();
  const [updatePollStatusMitation, { isLoading: isUpdateStatusLoading }] = useUpdatePollStatusMutation();
  const [deletePollQuery, { isLoading: isDelateLoading }] = useDeletePollMutation();

  useEffect(() => {
    if (currentPoll?.[0]) {
      setTitle(currentPoll[0].title);
      setPollQuestions(currentPoll[0].questions);
      setAuto(currentPoll[0].startDate ? true : false);
      setText(currentPoll[0].content);
      setDivisions(currentPoll[0].divisions);
      setOrganizations(currentPoll[0].organizations);
      if (currentPoll[0].startDate) {
        setStartTime(moment(currentPoll[0].startDate).utc().format(PICKER_TIME_MASK));
        setStartDate(moment(currentPoll[0].startDate).utc().format(PICKER_DATE_MASK));
      }
      if (currentPoll[0].stopDate) {
        setEndTime(moment(currentPoll[0].stopDate).utc().format(PICKER_TIME_MASK));
        setEndDate(moment(currentPoll[0].stopDate).utc().format(PICKER_DATE_MASK));
      }
    }
  }, [currentPoll]);

  const onTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTitle(event.target.value);
    },
    [dispatch],
  );

  const onTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setText(event.target.value);
    },
    [dispatch],
  );

  const validatePoll = (): boolean => {
    let isDateCorrect;

    if (auto) {
      isDateCorrect = !!(startDate && startTime && endDate && endTime);
    } else {
      isDateCorrect = true;
    }
    return !!title && !!text && isDateCorrect && !!pollQuestions.length;
  };

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    navigate('/polls');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    setOpenCanelModal(true);
  };

  const autoPollToggled = () => {
    setAuto(prev => !prev);
  };

  const preparePollToSave = () => {
    const preparedPollQuestions = pollQuestions.map(elem => {
      const { answerType, answers, isYourAnswer, title } = elem;
      const preparedAnswers = answers.map(item => item.value);
      return {
        answerType,
        answers: preparedAnswers,
        isYourAnswer,
        title,
      };
    });

    const poll: IPollUpdateRequest = {
      id: Number(currentPoll?.[0]?.id),
      title,
      content: text,
      startDate: checkDate(startDate as string, startTime as string, auto),
      stopDate: checkDate(endDate as string, endTime as string, auto),
      organizationIds: organizations?.filter(elem => elem !== null).map(elem => elem?.id),
      divisionIds: divisions?.filter(elem => elem !== null).map(elem => elem?.id),
      questions: preparedPollQuestions,
    };
    return poll;
  };

  const navigateAfterSuccess = (tab: PollTabs) => {
    return () => {
      dispatch(uiModel.Tabs.actions.setActive(tab));
      navigate('/polls');
    };
  };

  const updatePoll = async () => {
    addNotifications(
      updatePollQuery(preparePollToSave()),
      'Опрос изменен',
      'Ошибка при изменении опроса',
      navigateAfterSuccess(PollTabs.UNPUBLICATED),
    );
  };

  const changeDivisions = (divisionsToChange: TDivisionResponse[]) => {
    setDivisions(divisionsToChange);
  };

  const changeOrganizations = (organizationsToChange: TOrganizationResponse[]) => {
    setOrganizations(organizationsToChange);
  };

  const deletePoll = async () => {
    await addNotifications(
      deletePollQuery({ id: Number(id) }),
      'Опрос удален',
      'Ошибка при удалении опроса',
      navigateAfterSuccess(PollTabs.PUBLICATED),
    );
  };

  const changePollStatus = async () => {
    await addNotifications(
      updatePollStatusMitation({ id: Number(id), status: PollTabs.PUBLICATED }),
      'Опрос опубликован',
      'Ошибка при публикации опроса',
      navigateAfterSuccess(PollTabs.PUBLICATED),
    );
  };

  const onDrawerClose = () => {
    setIsDrawerCreateOpen(false);
  };

  const onCreate = (data: TQuestionInPoll) => {
    setPollQuestions([...pollQuestions, data]);
  };

  const openDrawer = () => {
    setIsDrawerCreateOpen(true);
  };

  const onDelete = (id: number) => {
    const filteredQuestions = pollQuestions.filter(elem => elem?.id !== id);
    setPollQuestions(filteredQuestions);
  };

  const onUpdate = (data: TQuestionInPoll) => {
    const updatedPollQuestions = pollQuestions.map(elem => {
      if (elem?.id === data.id) {
        return data;
      }
      return elem;
    });
    setPollQuestions(updatedPollQuestions);
  };

  return (
    <div className={style.wrap}>
      <div className={style['top-block']}>
        <h3>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          <Button danger onClick={deletePoll} loading={isDelateLoading}>
            Удалить
          </Button>
          <Button type="primary" onClick={changePollStatus} loading={isUpdateStatusLoading}>
            Опубликовать
          </Button>
          <Button onClick={cancelModalOpen}>Отмена</Button>
        </div>
        <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
      </div>
      <RequredField onTitleChange={onTitleChange} title={title} />
      <RequredField
        onTitleChange={onTextChange}
        title={text}
        label="Текст опроса"
        isTextarea
        placeholder="Введите текст опроса"
      />
      <QuestionsSection onOpen={openDrawer} questions={pollQuestions} onDelete={onDelete} onUpdate={onUpdate} />
      <QuestionDrawer isOpen={isDrawerCreateOpen} onClose={onDrawerClose} onCreate={onCreate} />

      <PickDivisions
        selectedDivisions={divisions}
        addDivisions={changeDivisions}
        addOrganizations={changeOrganizations}
        selectedOrganizations={organizations}
      />
      <div className={style.date}>
        <div className={style.deferred}>
          <Checkbox onChange={autoPollToggled} checked={auto}>
            Начало/окончание опроса по дате
          </Checkbox>
        </div>
        {auto ? (
          <PollDates
            onEndDateChange={setEndDate}
            onEndTimeChange={setEndTime}
            onStartDateChange={setStartDate}
            onStartTimeChange={setStartTime}
            startDate={startDate as string}
            endDate={endDate as string}
            startTime={startTime as string}
            endTime={endTime as string}
          />
        ) : null}
      </div>
      <div className={style['create-btn']}>
        <Button type="primary" loading={isUpdateLoading} onClick={updatePoll} disabled={!validatePoll()}>
          Сохранить
        </Button>
      </div>
    </div>
  );
};

export default UpdatePoll;
